/**
 * Hook for managing ReviewSection form state
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { NO_ASSIGNED_ANALYST, toAssignedAnalyst } from "../assignedAnalyst";

import type { AssignedAnalyst } from "../assignedAnalyst";
import type {
  RfpDetailsResponse,
  SendRfpBrokerageDetails,
  SendRfpClientDetails,
} from "../api";

// ============================================================================
// Editable Form State Types
// ============================================================================

export interface EditableClientFields {
  name: string;
  addressStreet: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZipcode: string;
  situsState: string;
  numberOfEmployees: string;
  hasInternationalEmployees: string;
  benAdminSystem: string;
  technologyCreditNeeded: string;
  techCreditType: string;
  techCreditAmount: string;
}

export interface EditableBrokerageFields {
  name: string;
  addressStreet: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZipcode: string;
  submitterExternalId: string;
  submitterName: string;
  submitterEmail: string;
  producerFirstName: string;
  producerLastName: string;
  producerEmail: string;
}

export interface EditableRfpFields {
  carrierDueDate: string;
  assignedAnalyst: AssignedAnalyst;
}

export interface EditedReviewFields {
  rfp: EditableRfpFields;
  client: EditableClientFields;
  brokerage: EditableBrokerageFields;
}

// ============================================================================
// Default Values (internal)
// ============================================================================

/**
 * Creates initial RFP fields from props
 */

interface UseReviewSectionStateParams {
  clientDetails?: SendRfpClientDetails;
  brokerageDetails?: SendRfpBrokerageDetails;
  carrierDueDate?: string;
  rfpDetails?: RfpDetailsResponse;
}

interface UseReviewSectionStateResult {
  // State
  rfpFields: EditableRfpFields;
  clientFields: EditableClientFields;
  brokerageFields: EditableBrokerageFields;

  // Updaters
  updateRfpField: <K extends keyof EditableRfpFields>(
    field: K,
    value: EditableRfpFields[K],
  ) => void;
  updateClientField: <K extends keyof EditableClientFields>(
    field: K,
    value: EditableClientFields[K],
  ) => void;
  updateBrokerageField: <K extends keyof EditableBrokerageFields>(
    field: K,
    value: EditableBrokerageFields[K],
  ) => void;

  // Resetters (revert to last API-synced values)
  resetRfpFields: () => void;
  resetClientFields: () => void;
  resetBrokerageFields: () => void;
}

/**
 * Maps address from API to form fields, defaulting nulls to empty strings
 */
function mapAddressToFields(
  address: SendRfpClientDetails["address"],
): Pick<
  EditableClientFields,
  | "addressStreet"
  | "addressLine2"
  | "addressCity"
  | "addressState"
  | "addressZipcode"
> {
  return {
    addressStreet: address?.addressLine1 ?? "",
    addressLine2: address?.addressLine2 ?? "",
    addressCity: address?.city ?? "",
    addressState: address?.state ?? "",
    addressZipcode: address?.zipCode ?? "",
  };
}

/**
 * Maps client details from API to editable form fields
 */
function mapClientDetailsToFields(
  details: SendRfpClientDetails,
): EditableClientFields {
  return {
    name: details.name ?? "",
    ...mapAddressToFields(details.address),
    situsState: details.situsState ?? "",
    numberOfEmployees: details.numberOfEmployees?.toString() ?? "",
    hasInternationalEmployees: details.hasInternationalEmployees ? "Yes" : "No",
    benAdminSystem: details.benAdminSystem ?? "",
    technologyCreditNeeded: details.technologyCreditNeeded ? "Yes" : "No",
    techCreditType: details.techCreditType ?? "",
    techCreditAmount: details.techCreditAmount?.toString() ?? "",
  };
}

/**
 * Extracts address fields from a brokerage address object
 */
function extractBrokerageAddress(address: SendRfpBrokerageDetails["address"]): {
  addressStreet: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZipcode: string;
} {
  return {
    addressStreet: address?.address ?? "",
    addressLine2: address?.addressLine2 ?? "",
    addressCity: address?.city ?? "",
    addressState: address?.state ?? "",
    addressZipcode: address?.zipcode ?? "",
  };
}

/**
 * Maps brokerage details from API to editable form fields
 */
function mapBrokerageDetailsToFields(
  details: SendRfpBrokerageDetails,
): EditableBrokerageFields {
  return {
    name: details.name ?? "",
    ...extractBrokerageAddress(details.address),
    submitterExternalId: "",
    submitterName: details.submitter?.name ?? "",
    submitterEmail: details.submitter?.email ?? "",
    producerFirstName: details.producer?.firstName ?? "",
    producerLastName: details.producer?.lastName ?? "",
    producerEmail: details.producer?.email ?? "",
  };
}

const CLIENT_INITIAL_FIELDS: EditableClientFields = {
  name: "",
  addressStreet: "",
  addressLine2: "",
  addressCity: "",
  addressState: "",
  addressZipcode: "",
  situsState: "",
  numberOfEmployees: "",
  hasInternationalEmployees: "",
  benAdminSystem: "",
  technologyCreditNeeded: "",
  techCreditType: "",
  techCreditAmount: "",
};

const BROKERAGE_INITIAL_FIELDS: EditableBrokerageFields = {
  name: "",
  addressStreet: "",
  addressLine2: "",
  addressCity: "",
  addressState: "",
  addressZipcode: "",
  submitterExternalId: "",
  submitterName: "",
  submitterEmail: "",
  producerFirstName: "",
  producerLastName: "",
  producerEmail: "",
};

/**
 * Manages ReviewSection form state with synchronization to parent
 */
export function useReviewSectionState({
  clientDetails,
  brokerageDetails,
  carrierDueDate,
  rfpDetails,
}: UseReviewSectionStateParams): UseReviewSectionStateResult {
  // Track if we've initialized RFP fields from props
  const initializedRfpFields = useRef(false);

  // RFP Details state
  const [rfpFields, setRfpFields] = useState<EditableRfpFields>({
    carrierDueDate: carrierDueDate ?? "",
    assignedAnalyst: NO_ASSIGNED_ANALYST,
  });

  // Client fields state
  const [clientFields, setClientFields] = useState<EditableClientFields>(
    CLIENT_INITIAL_FIELDS,
  );

  // Brokerage fields state
  const [brokerageFields, setBrokerageFields] =
    useState<EditableBrokerageFields>(BROKERAGE_INITIAL_FIELDS);

  // Sync RFP fields when props change (initial load from API)
  useEffect(() => {
    // Only sync on initial load when we get data from API
    if (!initializedRfpFields.current && rfpDetails) {
      setRfpFields({
        carrierDueDate: carrierDueDate ?? "",
        assignedAnalyst: toAssignedAnalyst(rfpDetails),
      });
      initializedRfpFields.current = true;
    }
  }, [carrierDueDate, rfpDetails]);

  // Sync client fields when props change
  useEffect(() => {
    if (clientDetails) {
      setClientFields(mapClientDetailsToFields(clientDetails));
    } else {
      setClientFields(CLIENT_INITIAL_FIELDS);
    }
  }, [clientDetails]);

  // Sync brokerage fields when props change
  useEffect(() => {
    if (brokerageDetails) {
      setBrokerageFields(mapBrokerageDetailsToFields(brokerageDetails));
    } else {
      setBrokerageFields(BROKERAGE_INITIAL_FIELDS);
    }
  }, [brokerageDetails]);

  // Helper to update RFP fields
  const updateRfpField = useCallback(
    <K extends keyof EditableRfpFields>(
      field: K,
      value: EditableRfpFields[K],
    ) => {
      setRfpFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Helper to update individual client fields
  const updateClientField = useCallback(
    <K extends keyof EditableClientFields>(
      field: K,
      value: EditableClientFields[K],
    ) => {
      setClientFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Helper to update individual brokerage fields
  const updateBrokerageField = useCallback(
    <K extends keyof EditableBrokerageFields>(
      field: K,
      value: EditableBrokerageFields[K],
    ) => {
      setBrokerageFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Reset to last API-synced values
  const resetRfpFields = useCallback(() => {
    setRfpFields({
      carrierDueDate: carrierDueDate ?? "",
      assignedAnalyst: rfpDetails
        ? toAssignedAnalyst(rfpDetails)
        : NO_ASSIGNED_ANALYST,
    });
  }, [carrierDueDate, rfpDetails]);

  const resetClientFields = useCallback(() => {
    if (clientDetails) {
      setClientFields(mapClientDetailsToFields(clientDetails));
    }
  }, [clientDetails]);

  const resetBrokerageFields = useCallback(() => {
    if (brokerageDetails) {
      setBrokerageFields(mapBrokerageDetailsToFields(brokerageDetails));
    }
  }, [brokerageDetails]);

  return {
    rfpFields,
    clientFields,
    brokerageFields,
    updateRfpField,
    updateClientField,
    updateBrokerageField,
    resetRfpFields,
    resetClientFields,
    resetBrokerageFields,
  };
}

// ============================================================================
// Formatting Helpers
// ============================================================================

/**
 * Format effective date for display
 */
export function formatEffectiveDate(effectiveDate?: string): string {
  if (!effectiveDate) return "—";
  try {
    const date = new Date(effectiveDate);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return effectiveDate;
  }
}

/**
 * Format carrier due date for display
 */
export function formatCarrierDueDate(dateValue?: string): string {
  if (!dateValue) return "";
  try {
    const date = new Date(dateValue);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return dateValue;
  }
}

/**
 * Format address fields for display (shared by client and brokerage)
 */
function formatAddressFields(fields: {
  addressStreet: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZipcode: string;
}): { street: string; cityStateZip: string } {
  const streetParts = [fields.addressStreet, fields.addressLine2].filter(
    Boolean,
  );
  const cityStateZipParts = [fields.addressCity, fields.addressState].filter(
    Boolean,
  );
  const cityState = cityStateZipParts.join(", ");
  const cityStateZip = fields.addressZipcode
    ? `${cityState} ${fields.addressZipcode}`
    : cityState;
  return {
    street: streetParts.length > 0 ? streetParts.join(", ") : "—",
    cityStateZip,
  };
}

/**
 * Format brokerage address for display
 */
export function formatBrokerageAddress(fields: EditableBrokerageFields): {
  street: string;
  cityStateZip: string;
} {
  return formatAddressFields(fields);
}

/**
 * Format client address for display from individual fields
 */
export function formatClientAddress(fields: EditableClientFields): {
  street: string;
  cityStateZip: string;
} {
  return formatAddressFields(fields);
}
